import * as React from 'react';

import { 
    Button, 
    Box, 
    Typography, 
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Alert,
    Paper,
    Stack,
} from '@mui/material';
import Checkbox from '@mui/material/Checkbox';

import { Confirmation, InputDialog } from '../../utils';
import { TokenValidator, CONST, LoggerUtils, TS_CONSTS} from '../../../utils';
import { Parts, Logger, Candid, Orders, Gugle, MaterialTraceability} from '../../../controllers';
import MaterialTraceabilityPanel from './materialTraceabilityPanel';
import useLegacyAction,{requireLegacySuccess} from './legacyAction';
import {
    INVALID_ORDER_ID_MESSAGE,
    createScanRequestGate,
    normalizeOrderId,
} from '../../../utils/orderScanner';

const createIdempotencyKey = () => window.crypto && window.crypto.randomUUID
    ? window.crypto.randomUUID()
    : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, value => {
        const random = Math.random() * 16 | 0;
        return (value === 'x' ? random : ((random & 3) | 8)).toString(16);
    });

const parseMachiningPieceCount = (value, maximum) => {
    const text = String(value === undefined || value === null ? '' : value).trim();
    if (!/^\d+$/.test(text)) return null;
    const parsed = Number(text);
    const max = Number(maximum);
    if (!Number.isSafeInteger(parsed) || parsed < 1 || !Number.isSafeInteger(max) || parsed > max) return null;
    return parsed;
};

export default function OrderReader({AreaInfo,initialOrder,onBusy}) {
    const [legacyBusy,setLegacyBusy]=React.useState(false);
    const legacyBusyRef=React.useRef(false);
    const runLegacy=useLegacyAction(value=>{legacyBusyRef.current=value;setLegacyBusy(value);onBusy?.(value);},()=>({order:selected}));

    const isMachining = AreaInfo.data.name === 'Machining';

    const [order, setOrder] = React.useState('');
    const [selected, setSelected] = React.useState('');
    const [parts, setParts] = React.useState([]);
    const [errors, setErrors] = React.useState([]);
    const [errorLog, setErrorLog] = React.useState('');
    const [scanPending, setScanPending] = React.useState(false);
    const scanGate = React.useRef(createScanRequestGate());

    const [co, isCo] = React.useState(false);
    const [coType, setCoType]  = React.useState(0);
    const [coNumber, setCoNumber] = React.useState('');
    const [coData, setCoData] = React.useState({
        dhr: 0,
        counter : 0
    })

    //UI Confirmations
    const [error, showError] = React.useState(false);
    const [processing, showProcessing] = React.useState(false);
    const [errorInput, showErrorInput] = React.useState(false);

    //CO-UI Confirmations
    const [coTypeUi, showCoTypeUi] = React.useState(false);
    const [coProxes, showCoProxes] = React.useState(false);

    // Material Traceability (Machining only)
    const [traceState, setTraceState] = React.useState({machine: null, activeRoll: null, ready: false, isAdmin: false});
    const [tracePreview, setTracePreview] = React.useState(null);
    const [tracePieceCount, setTracePieceCount] = React.useState('');
    const [registeredTrace, setRegisteredTrace] = React.useState(null);
    const [recentDuplicate, showRecentDuplicate] = React.useState(false);
    const [traceRefreshKey, setTraceRefreshKey] = React.useState(0);
    const [traceScanPending, setTraceScanPending] = React.useState(false);
    const [traceProcessPending, setTraceProcessPending] = React.useState(false);
    const attemptKey = React.useRef(null);
    const processPendingRef = React.useRef(false);
    const scannerRef = React.useRef(null);
    const updateTraceState = React.useCallback(value => setTraceState(value), []);

    //Gugle Stuffing
    const gugleInit = React.useRef(false);

    React.useEffect(() => {
        if(!gugleInit.current){
            gugleInit.current = true;
            handleGugleData();
        }
    });

    React.useEffect(() => {
        if (!tracePreview) return;
        const currentMachine = traceState.machine && Number(traceState.machine.id);
        const previewMachine = tracePreview.machine && Number(tracePreview.machine.id);
        const currentAssignment = traceState.activeRoll && Number(traceState.activeRoll.assignmentId);
        const previewAssignment = tracePreview.activeRoll && Number(tracePreview.activeRoll.assignmentId);
        if (currentMachine !== previewMachine || currentAssignment !== previewAssignment) {
            setTracePreview(null);
            setTracePieceCount('');
            setParts([]);
            setErrors([]);
            setSelected('');
            isCo(false);
            attemptKey.current = null;
        }
    }, [traceState.machine, traceState.activeRoll, tracePreview]);

    const handleGugleData = async() => {
        const infoResponse = await Gugle.GetGugle();
        
        if(infoResponse === undefined){
            return undefined;
        }

        if(!infoResponse.found){
            const linkReponse = await Gugle.GetAuthLink();
            window.open(linkReponse.url,"", "width=1500,height=1000");
            return undefined;
        }

        const refreshResponse = await Gugle.RefreshToken({data: infoResponse.result.info})
        console.log('Loaded', refreshResponse);
        return refreshResponse.info;
    }

    const handleFetch = async(e,inputOrder=order) => {
        e.preventDefault();
        if(legacyBusyRef.current)return;
        if(!scanGate.current.tryStart()) return;

        setScanPending(true);
        setParts([]);setSelected('');setErrors([]);isCo(false);
        setTracePreview(null);setTracePieceCount('');
        if(isMachining) setTraceScanPending(true);
        try {
            let scannedOrder;
            try {
                scannedOrder = normalizeOrderId(inputOrder);
            } catch (_) {
                alert(INVALID_ORDER_ID_MESSAGE);
                return;
            }

            isCo(false);
            setRegisteredTrace(null);

            if(isMachining){
                if(processPendingRef.current) return;
                if(!traceState.ready || !traceState.machine){
                    alert('Select an ATTLAS machine with an Active Roll before scanning an order.');
                    return;
                }
                const token = await TokenValidator.GetStaffUser();
                const response = await MaterialTraceability.OrderInfo(token, traceState.machine.id, scannedOrder);
                if(!response || !response.success){
                    alert(response && response.message ? response.message : 'Error while fetching material traceability order information');
                    return;
                }
                const info = response.result;
                setTracePreview(info);
                setTracePieceCount(String(info.pieceCount));
                setSelected(info.orderId);
                setErrors([]);
                setErrorLog('');
                attemptKey.current = createIdempotencyKey();
                if(info.source === 'CANDID'){
                    isCo(true);
                    setParts([]);
                    setCoNumber(info.caseId);
                }else{
                    setParts(info.parts);
                    setCoNumber('');
                }
                return;
            }

            const status = AreaInfo.data.expected;
            const token = await TokenValidator.GetStaffUser();
            const result = await Parts.GetTracking(token, scannedOrder, status);

            if(result === undefined){
                alert('Error while fetching order information');
                return;
            }

            if(!result.success){
                if(result.message){
                    alert(result.message);
                }else{
                    alert('Error while fetching order information')
                }
                return;
            }

            if(result.result.length < 1){

                const response = await Candid.GetTracingReportByOrder(scannedOrder);

                if(response === undefined){
                    alert('Error al leer Orden');
                    return;
                }

                if(response.success && response.found){

                    isCo(true);
                    setParts([]);
                    setSelected(scannedOrder);
                    setCoNumber(response.result.case_serial);
                    return;
                }

                alert('Orden escaneada no valida');
                return;
            }

            setParts(result.result);
            setSelected(scannedOrder);
        } catch (_) {
            alert('No se pudo consultar la orden. Intenta nuevamente; no se registró un escaneo.');
        } finally {
            setOrder('');
            setScanPending(false);
            setTraceScanPending(false);
            scanGate.current.finish();
            window.setTimeout(() => scannerRef.current?.focus(), 0);
        }
    }

    const initialLoaded=React.useRef(false),initialFetch=React.useRef(handleFetch);
    initialFetch.current=handleFetch;
    React.useEffect(()=>{
        if(!initialOrder||initialLoaded.current||(isMachining&&!traceState.ready))return;
        initialLoaded.current=true;
        initialFetch.current({preventDefault:()=>{}},initialOrder.orderNumber);
    },[initialOrder,isMachining,traceState.ready]);
    React.useEffect(()=>{onBusy?.(legacyBusy||scanPending||traceScanPending||traceProcessPending);},[onBusy,legacyBusy,scanPending,traceScanPending,traceProcessPending]);

    const clearAfterMachining = trace => {
        showProcessing(false);
        showCoProxes(false);
        setParts([]);
        setErrors([]);
        setErrorLog('');
        isCo(false);
        setOrder('');
        setSelected('');
        setCoType(0);
        setCoNumber('');
        setCoData({dhr: 0, counter: 0});
        setTracePreview(null);
        setTracePieceCount('');
        setRegisteredTrace(trace);
        attemptKey.current = null;
        setTraceRefreshKey(value => value + 1);
        window.setTimeout(() => scannerRef.current && scannerRef.current.focus(), 0);
    }

    const handleMachiningProcessing = async(confirmRecent = false, useNewKey = false) => {
        if(processPendingRef.current) return;
        if(!tracePreview || !traceState.ready || !traceState.machine){
            alert('Material Traceability is not ready. Scan the order again.');
            return;
        }
        const selectedPieceCount = parseMachiningPieceCount(tracePieceCount, tracePreview.pieceCount);
        if(selectedPieceCount === null){
            alert(`Pieces must be a whole number from 1 to ${tracePreview.pieceCount}.`);
            return;
        }
        processPendingRef.current = true;
        setTraceProcessPending(true);
        try {
            const info = await handleGugleData();
            if(info === undefined){
                alert('Error while getting Google Credentials');
                return;
            }
            if(useNewKey || !attemptKey.current) attemptKey.current = createIdempotencyKey();
            const token = await TokenValidator.GetStaffUser();
            const payload = {
                orderId: tracePreview.orderId,
                machineId: traceState.machine.id,
                idempotencyKey: attemptKey.current,
                confirmRecent,
                pieceCount: selectedPieceCount,
                errors,
                errorLog
            };

            const response = await MaterialTraceability.Process(token, payload);
            if(!response || !response.success){
                if(response && response.code === 'RECENT_DUPLICATE'){
                    showRecentDuplicate(true);
                    return;
                }
                alert(response && response.message ? response.message : 'Material traceability processing failed. No changes were committed.');
                return;
            }

            const targetAreaTab = TS_CONSTS.AREA_TABS[AreaInfo.data.name];
            if(targetAreaTab !== undefined && response.result.reportParts > 0){
                const filePayload = {
                    data: info,
                    payload: {
                        number: tracePreview.orderId,
                        parts: response.result.reportParts,
                        tag: response.result.tag,
                        sheet: targetAreaTab
                    }
                };
                await Gugle.ReportsRebeca001(filePayload);
            }
            clearAfterMachining(response.result.trace);
        } finally {
            processPendingRef.current = false;
            setTraceProcessPending(false);
            window.setTimeout(() => scannerRef.current && scannerRef.current.focus(), 0);
        }
    }

    const handleProcessing = async() => {
        if(scanGate.current.isPending())return;
        if(isMachining){
            await handleMachiningProcessing(false);
            return;
        }
        return processLegacyParts();
    };
    const processLegacyParts=runLegacy(async()=>{
        const info = await handleGugleData();
        const token = await TokenValidator.GetStaffUser();
        const targetAreaTab = TS_CONSTS.AREA_TABS[AreaInfo.data.name];
        var partCounter = 0;
        var tag = undefined;

        if(info === undefined){
            alert('Error while getting Google Credentials');
            return;
        }

        for(const part of parts){
            const hasError = errors.includes(part.id);
            partCounter += hasError ? 0 : 1;
            tag = part.tag;
            const logType = hasError ? 'Error' : 'Update'
            const logTitle = `Tracking Station ${AreaInfo.data.name} : ${logType}`
            const logInfo = hasError ? errorLog : 'Part processed through area successfully'

            const status = hasError ? AreaInfo.data.error : part.data_general.type != 'Aligner' ? AreaInfo.data.no_aligner : AreaInfo.data.given;
            const payload = {
                id          : part.id,
                status      : status,
            }

            const log = await LoggerUtils.PayloadCreator( logType, logTitle, logInfo);
            const response = await Parts.UpdateStatus(token, {...payload,audit:log});
            requireLegacySuccess(response);
        }

        if(targetAreaTab != undefined && partCounter > 0){
            const filePayload = {
                data : info,
                payload : {
                    number : selected,
                    parts  : partCounter,
                    tag    : tag,
                    sheet  : targetAreaTab
                }
            }
            const fileRespose = await Gugle.ReportsRebeca001(filePayload);
        }
        
        showProcessing(false);
        setParts([]);
        setErrors([]);

        alert('Order Processed successfully');
    },{write:true});

    const handleError = async(e) => {
        var delta = [...errors];
        const value = parseInt(e.target.value);
        
        if(e.target.checked){
            delta.push(value);
        }else{
            delta = delta.filter(x => x != value)
        }
        
        setErrors(delta);
        setErrorLog('');
    }

    const EvaluateError = async() => {
        if(errors.length > 0){
            showErrorInput(true);
            return;
        }

        showProcessing(true);
    }

    const handleCounters = async(e) => {
        setCoData({...coData, [e.target.id]:parseInt(e.target.value)})
    }

    const handleCoType = async(type) => {
        setCoType(type);
        showCoTypeUi(false);
        showCoProxes(true);
    }

    const handleCoProcessing = async() =>{
        if(scanGate.current.isPending())return;
        if(isMachining){
            await handleMachiningProcessing(false);
            return;
        }
        return processLegacyCandid();
    };
    const processLegacyCandid=runLegacy(async()=>{
        showCoProxes(false);
        const info = await handleGugleData();
        const targetAreaTab = TS_CONSTS.AREA_TABS[AreaInfo.data.name];

        if(info === undefined){
            alert('Error while getting Google Credentials');
            return;
        }

        const payload = {
            order : coNumber,
            data : {
                area : {
                    name : AreaInfo.data.name,
                    id : coType === 0 ? AreaInfo.data.candid_id : AreaInfo.data.no_aligner_co,
                },
                number : selected,
                counter : coData
            }
        }

        const response = await Candid.CreateTracing(payload);
        requireLegacySuccess(response);

        if(response == undefined){
            alert('Error Processing Order. \nPlease, try again');
            return;
        }

        if(!response.success){
            alert('Processing Order fail. \nPlease, try again');
            return;
        }

        if(targetAreaTab != undefined){
            const filePayload = {
                data : info,
                payload : {
                    number : selected,
                    parts  : coData.counter,
                    tag    : coNumber,
                    sheet  : targetAreaTab
                }
            }
            const fileRespose = await Gugle.ReportsRebeca001(filePayload);
        }

        isCo(false);
        setOrder('');
        setCoType(0);
        setCoNumber('');
        setCoData({
            dhr: 0,
            counter : 0
        });

        alert('Order Processed successfully');
    },{write:true});

    const selectedTracePieceCount = tracePreview
        ? parseMachiningPieceCount(tracePieceCount, tracePreview.pieceCount)
        : null;
    const tracePieceCountInvalid = Boolean(tracePreview) && selectedTracePieceCount === null;

    return(
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <Confirmation 
                open={error}
                onClose={() => showError(false)}
                onConfirm={() => showError(false)}
                title={'Wrong Scan'}
                msg={'Please Scan the numeric barcode (Order ID)'}
                info={true}
            />

            <InputDialog 
                open={errorInput}
                closer={() => showErrorInput(false)}
                fetcher={setErrorLog}
                action={ () => showProcessing(true)}
                title={'Error Description'}
                msg={'Enter error description in the field below'}
            />

            <Confirmation 
                open={processing}
                onClose={() => showProcessing(false)}
                onConfirm={handleProcessing}
                title={'Confirmation'}
                msg={isMachining && tracePreview
                    ? `The parts will be updated and traceability will record ${selectedTracePieceCount} pieces.`
                    : 'The parts will be updated with the given input'}
                info={false}
            />

            <Confirmation 
                open={coProxes}
                onClose={() => showCoProxes(false)}
                onConfirm={handleCoProcessing}
                title={'Confirmation'}
                msg={'The parts will be updated with the given input'}
                info={false}
            />

            <Confirmation 
                open={coTypeUi}
                onClose={() => handleCoType(1)}
                onConfirm={() => handleCoType(0)}
                title={'Tipo de Pieza'}
                msg={'Las piezas son alineadores'}
                cancelTxt='No'
                confirmTxt='Si'
            />

            <Confirmation
                open={recentDuplicate}
                onClose={() => showRecentDuplicate(false)}
                onConfirm={() => {
                    showRecentDuplicate(false);
                    handleMachiningProcessing(true, true);
                }}
                title='Recent duplicate scan'
                msg='This order was registered on the same machine and roll moments ago. Confirm only for a legitimate reprocess.'
                cancelTxt='Cancel'
                confirmTxt='Confirm Reprocess'
            />

            {isMachining && <MaterialTraceabilityPanel onStateChange={updateTraceState} refreshKey={traceRefreshKey} />}

            {registeredTrace && <Alert severity='success' sx={{mb: 1}}>
                REGISTERED — Order {registeredTrace.orderId}, Case {registeredTrace.caseId}, {registeredTrace.client},
                {' '}{registeredTrace.pieces} pieces, {registeredTrace.machine}, Roll {registeredTrace.rollNumber}
            </Alert>}

            <Box 
                component='form'
                onSubmit={handleFetch}
            >
                <TextField
                    margin="normal"
                    required
                    fullWidth
                    label="ESCANEA :  Order Id"
                    id="order"
                    name="order"
                    autoFocus
                    inputRef={scannerRef}
                    value={order}
                    onChange={e => setOrder(e.target.value)}
                    disabled={scanPending || (isMachining && (!traceState.ready || traceScanPending || traceProcessPending))}
                />
            </Box>

            <Divider 
                sx={{
                    marginTop:1,
                    minWidth:'95vw',
                    borderColor:CONST.Colors.Primary.Main
                }}
            />

            {isMachining && tracePreview && <Paper variant='outlined' sx={{p: 2, mt: 2}}>
                <Typography variant='h6'>Order Information</Typography>
                <Stack direction={{xs: 'column', md: 'row'}} gap={3} sx={{mt: 1}}>
                    <Typography><strong>Order:</strong> {tracePreview.orderId}</Typography>
                    <Typography><strong>Case:</strong> {tracePreview.caseId}</Typography>
                    <Typography><strong>Source:</strong> {tracePreview.source}</Typography>
                    <Typography><strong>Client:</strong> {tracePreview.client}</Typography>
                    <TextField
                        label='Pieces'
                        type='number'
                        size='small'
                        value={tracePieceCount}
                        onChange={event => setTracePieceCount(event.target.value)}
                        error={tracePieceCountInvalid}
                        helperText={tracePieceCountInvalid
                            ? `Enter a whole number from 1 to ${tracePreview.pieceCount}`
                            : `Calculated total: ${tracePreview.pieceCount}`}
                        inputProps={{min: 1, max: tracePreview.pieceCount, step: 1, inputMode: 'numeric'}}
                        disabled={traceScanPending || traceProcessPending}
                        sx={{width: {xs: '100%', sm: 220}}}
                    />
                </Stack>
            </Paper>}

            {parts.length > 0 && (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            padding: 2,
                        }}
                    >
                        <Typography variant='h6'>
                            Order ID:{selected}
                        </Typography>
                        <Button
                            color='success'
                            variant='contained'
                            onClick={EvaluateError}
                            disabled={isMachining && (!traceState.ready || traceScanPending || traceProcessPending || tracePieceCountInvalid)}
                        >
                            Process Parts
                        </Button>
                    </Box>
                        
                    <Divider 
                        sx={{
                            minWidth:'95vw',
                            borderColor:CONST.Colors.Primary.Main
                        }}
                    />

                    <TableContainer sx={{mt:2}}>
                    <Table sx={{ minWidth: '90vw' }} aria-label="simple table">
                        <TableHead>
                        <TableRow >
                            <TableCell >TAG</TableCell>
                            <TableCell  align="center">TYPE</TableCell>
                            <TableCell  align="center">STEP</TableCell>
                            <TableCell  align="center">SIDE</TableCell>
                            <TableCell  align="center">ERROR</TableCell>
                        </TableRow>
                        </TableHead>
                        <TableBody>
                        {parts.map((row) => (
                            <TableRow
                            key={row.id}
                            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                            >
                                <TableCell component="th" scope="row">{row.tag}</TableCell>
                                <TableCell align="center">{row.data_general.type}</TableCell>
                                <TableCell align="center">{row.step}</TableCell>
                                <TableCell align="center">{row.side == 1 ? 'Upper' : 'Lower' }</TableCell>
                                <TableCell align="center">
                                    <Checkbox  color='secondary' value={row.id} onChange={handleError}/>
                                </TableCell>
                            </TableRow>
                        ))}
                        </TableBody>
                    </Table>
                    </TableContainer>
                </Box>
            )}

            {parts.length <= 0 && co && (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            padding: 2,
                        }}
                    >
                        <Typography variant='h6' sx={{alignContent:'center'}}>
                            Candid Order || Case ID: {coNumber}
                        </Typography>
                        
                        <Button
                            color='success'
                            variant='contained'
                            onClick={() => isMachining ? showCoProxes(true) : showCoTypeUi(true)}
                            disabled={isMachining && (!traceState.ready || traceScanPending || traceProcessPending || tracePieceCountInvalid)}
                        >
                            Process Parts
                        </Button>
                        
                    </Box>
                        
                    <Divider 
                        sx={{
                            minWidth:'95vw',
                            borderColor:CONST.Colors.Primary.Main
                        }}
                    />

                    {!isMachining && <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            gap: 2
                        }}
                    >
                        <TextField
                            type='number'
                            margin="normal"
                            required
                            fullWidth
                            label="Enter Counter"
                            id="counter"
                            name="counter"
                            value={coData.counter}
                            onChange={e => handleCounters(e)}
                        />
                    </Box>}
                    
                </Box>
            )}

        </Box>
    );
}
